import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, Award, CheckCircle } from "lucide-react";
import logo from "@/assets/logo.png";
import { footerLinks } from "@/lib/siteStructure";
 
 const certifications = [
   { icon: Shield, label: "Industry Compliant" },
   { icon: Award, label: "OSHA Aligned" },
   { icon: CheckCircle, label: "Quality Guaranteed" },
 ];
 
 export function Footer() {
   return (
     <footer className="bg-primary text-primary-foreground" role="contentinfo">
       {/* Trust bar */}
       <div className="border-b border-primary-foreground/10">
         <div className="container py-6">
           <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
             {certifications.map((cert) => (
               <div key={cert.label} className="flex items-center gap-2 text-sm">
                 <cert.icon className="h-5 w-5 text-accent" />
                 <span className="font-medium">{cert.label}</span>
               </div>
             ))}
           </div>
         </div>
       </div>
 
       {/* Main footer */}
        <div className="container py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-3 lg:col-span-2">
               <Link to="/" className="flex items-center mb-4">
                 <img src={logo} alt="PalletRackingUSA" className="h-14 w-auto" width="84" height="56" />
               </Link>
              <p className="text-primary-foreground/70 text-sm mb-6 max-w-sm">
                Your nationwide industrial brokerage partner. Connecting warehouse managers with quality racking systems at competitive prices.
              </p>
              <div className="space-y-3 text-sm">
                 <a href="tel:1-888-315-7225" className="flex items-center gap-2 hover:text-accent transition-colors">
                   <Phone className="h-4 w-4" />
                   1-888-315-RACK
                </a>
                <a href="mailto:info@palletrackingusa.com" className="flex items-center gap-2 hover:text-accent transition-colors">
                  <Mail className="h-4 w-4" />
                  info@palletrackingusa.com
                </a>
              </div>
               
               {/* Location badges */}
               <div className="pt-4 border-t border-primary-foreground/10 mt-4">
                 <p className="text-xs text-primary-foreground/50 mb-2">Regional Offices:</p>
                 <div className="flex flex-wrap gap-2">
                   <Link to="/locations/utah" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-foreground/10 text-xs hover:bg-accent/20 transition-colors">
                     <MapPin className="h-3 w-3" />
                     Utah
                   </Link>
                   <Link to="/locations/north-carolina" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-foreground/10 text-xs hover:bg-accent/20 transition-colors">
                     <MapPin className="h-3 w-3" />
                     N. Carolina
                   </Link>
                   <Link to="/locations/ontario" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-foreground/10 text-xs hover:bg-accent/20 transition-colors">
                     <MapPin className="h-3 w-3" />
                     Ontario 🇨🇦
                   </Link>
                 </div>
               </div>
            </div>

            {/* Products */}
            <div className="col-span-2">
              <h4 className="font-semibold mb-4">
                <Link to="/products" className="hover:text-accent transition-colors">Products</Link>
              </h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/60 mb-2">Selective Access</p>
                  <ul className="space-y-2">
                    {[
                      { label: "Selective Racking", href: "/products/selective" },
                      { label: "VNA Racking", href: "/products/vna" },
                      { label: "Cantilever", href: "/products/cantilever" },
                    ].map((link) => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/60 mb-2 mt-4">Portable</p>
                  <ul className="space-y-2">
                    {[
                      { label: "Stack Racks", href: "/products/stack-racks" },
                      { label: "Post Pallets", href: "/products/post-pallets" },
                    ].map((link) => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/60 mb-2">High-Density</p>
                  <ul className="space-y-2">
                    {[
                      { label: "Double-Deep", href: "/products/double-deep" },
                      { label: "Drive-In", href: "/products/drive-in" },
                      { label: "Drive-Through", href: "/products/drive-through" },
                      { label: "Push-Back", href: "/products/push-back" },
                      { label: "Pallet Flow", href: "/products/pallet-flow" },
                      { label: "Mobile", href: "/products/mobile" },
                    ].map((link) => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/60 mb-2 mt-4">Automated</p>
                  <ul className="space-y-2">
                    {[
                      { label: "Shuttle", href: "/products/shuttle" },
                      { label: "AS/RS", href: "/products/asrs" },
                      { label: "Clad Rack", href: "/products/clad-rack" },
                    ].map((link) => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Services + Comparisons */}
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2.5">
                {footerLinks.services.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <h4 className="font-semibold mb-3 mt-6">Comparisons</h4>
              <ul className="space-y-2.5">
                {footerLinks.comparisons.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources + Company */}
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2.5">
                {footerLinks.resources.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <h4 className="font-semibold mb-3 mt-6">Company</h4>
              <ul className="space-y-2.5">
                {footerLinks.company.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
 
       {/* Bottom bar */}
       <div className="border-t border-primary-foreground/10">
         <div className="container py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/60">
           <p>© {new Date().getFullYear()} PalletRackingUSA. All rights reserved.</p>
           <p>Nationwide Network • Competitive Pricing • Quality Guaranteed</p>
         </div>
       </div>
     </footer>
   );
 }